import { createElement, parseElement } from 'src/utils/dom-create';
import { htmlEscape } from 'src/utils/html-escape';

export const TOKEN_TYPE_LABEL = 'label';
export const TOKEN_TYPE_CONFIDENTIAL = 'confidential';
export const TOKEN_TYPE_ASSIGNEE = 'assigned';
export const TOKEN_TYPE_AUTHOR = 'author';
export const TOKEN_TYPE_SITE = 'site';
export const TOKEN_TYPE_ROLE = 'role';
export const TOKEN_TYPE_BEFORE = 'before';
export const TOKEN_TYPE_RANGE = 'range';
export const TOKEN_TYPE_AFTER = 'after';
export const TOKEN_TYPE_REPORTED_BEFORE = 'reported-before';
export const TOKEN_TYPE_REPORTED_RANGE = 'reported-range';
export const TOKEN_TYPE_REPORTED_AFTER = 'reported-after';
export const TOKEN_TYPE_RESOLVED_BEFORE = 'resolved-before';
export const TOKEN_TYPE_RESOLVED_RANGE = 'resolved-range';
export const TOKEN_TYPE_RESOLVED_AFTER = 'resolved-after';
export const TOKEN_TYPE_REL = 'rel';
export const TOKEN_TYPE_TITLE = 'title';
export const TOKEN_TYPE_INVALID = 'invalid';

export const TOKEN_TYPE_ALIAS_NAME = 'name';
export const TOKEN_TYPE_ALIAS_LOCATED_AT = 'located-at';
export const TOKEN_TYPE_ALIAS_ASSET_TYPE = 'asset-type';
export const TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE = 'employee-role';

// export const TOKEN_TYPE_ONLY = 'only'
// export const TOKEN_TYPE_MEASUREMENT_TYPE = 'measurement_type'

export const TOKEN_TYPES = [
  TOKEN_TYPE_LABEL,
  TOKEN_TYPE_CONFIDENTIAL,
  TOKEN_TYPE_ASSIGNEE,
  TOKEN_TYPE_AUTHOR,
  TOKEN_TYPE_SITE,
  TOKEN_TYPE_ROLE,
  TOKEN_TYPE_BEFORE,
  TOKEN_TYPE_AFTER,
  TOKEN_TYPE_RANGE,
  TOKEN_TYPE_REPORTED_AFTER,
  TOKEN_TYPE_REPORTED_BEFORE,
  TOKEN_TYPE_REPORTED_RANGE,
  TOKEN_TYPE_RESOLVED_AFTER,
  TOKEN_TYPE_RESOLVED_BEFORE,
  TOKEN_TYPE_RESOLVED_RANGE,
  TOKEN_TYPE_REL,
  TOKEN_TYPE_TITLE,
] as const;

const TOKEN_TYPE_ALIASES = [
  TOKEN_TYPE_ALIAS_NAME,
  TOKEN_TYPE_ALIAS_LOCATED_AT,
  TOKEN_TYPE_ALIAS_ASSET_TYPE,
  TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE,
] as const;

export const TOKEN_OPERATOR_EQUALS = '=';
export const TOKEN_OPERATOR_NEQUALS = '!=';
export const TOKEN_OPERATOR_ANY_OF = '||';
export const TOKEN_OPERATOR_INVALID = '∅';

export const TOKEN_OPERATORS = [
  TOKEN_OPERATOR_EQUALS,
  TOKEN_OPERATOR_NEQUALS,
  TOKEN_OPERATOR_ANY_OF,
] as const;

export type TokenType =
  | (typeof TOKEN_TYPES)[number]
  | (typeof TOKEN_TYPE_ALIASES)[number]
  | typeof TOKEN_TYPE_INVALID;
export type TokenOperator =
  (typeof TOKEN_OPERATORS)[number] | typeof TOKEN_OPERATOR_INVALID;

export function isValidTokenType(
  potential: string | null,
): potential is TokenType {
  return (
    TOKEN_TYPES.includes(
      potential as Exclude<
        TokenType,
        (typeof TOKEN_TYPE_ALIASES)[number] | typeof TOKEN_TYPE_INVALID
      >,
    ) ||
    TOKEN_TYPE_ALIASES.includes(
      potential as Exclude<
        TokenType,
        (typeof TOKEN_TYPES)[number] | typeof TOKEN_TYPE_INVALID
      >,
    )
  );
}

export function isValidOperator(
  potential: string | null,
): potential is TokenOperator {
  if (!potential) {
    return false;
  }

  return TOKEN_OPERATORS.includes(
    potential as Exclude<TokenOperator, typeof TOKEN_OPERATOR_INVALID>,
  );
}

export const TOKEN_DEFAULTS = {
  [TOKEN_TYPE_ASSIGNEE]: [
    { value: 'me', label: 'Me' },
    { value: 'nil', label: 'Unassigned' },
  ],

  [TOKEN_TYPE_AUTHOR]: [
    { value: 'me', label: 'Me' },
    { value: 'system', label: 'System' },
    { value: 'nil', label: 'Signed-out' },
  ],

  [TOKEN_TYPE_CONFIDENTIAL]: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
} as const;

export type TokenData = {
  id: string;
  type: TokenType;
  operator: TokenOperator;
  value: string;
};

export class Token {
  public readonly id: string;
  public labelledValue: Element | undefined;

  public constructor(
    public type: TokenType,
    public operator: TokenOperator,
    public value: string,
    labelledValue?: string | Element,
    idOverride?: string,
  ) {
    this.id = idOverride ?? `token-${Math.random().toString(36).substring(2)}`;

    if (typeof labelledValue === 'string') {
      if (labelledValue.startsWith(`html:`)) {
        const html = parseElement(labelledValue);
        this.labelledValue = html || undefined;
      } else {
        this.labelledValue = createElement('span', {}, labelledValue);
      }

      this.labelledValue?.classList.add('max-h-[16px]');
    }
  }

  public get typeLabel(): string {
    switch (this.type) {
      case TOKEN_TYPE_LABEL: {
        return 'Label';
      }
      case TOKEN_TYPE_CONFIDENTIAL: {
        return 'Confidential';
      }
      case TOKEN_TYPE_ASSIGNEE: {
        return 'Assignee';
      }
      case TOKEN_TYPE_AUTHOR: {
        return 'Author';
      }
      case TOKEN_TYPE_SITE: {
        return 'Site';
      }
      case TOKEN_TYPE_ROLE: {
        return 'Role';
      }
      case TOKEN_TYPE_REL: {
        return 'Link';
      }
      case TOKEN_TYPE_TITLE: {
        return 'Title';
      }
      case TOKEN_TYPE_ALIAS_ASSET_TYPE: {
        return 'Asset type';
      }
      case TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE: {
        return 'Employee role';
      }
      case TOKEN_TYPE_ALIAS_NAME: {
        return 'Name';
      }
      case TOKEN_TYPE_ALIAS_LOCATED_AT: {
        return 'Located at';
      }
    }

    return this.type;
  }

  public get hasComputedLabel(): boolean {
    return this.labelledValue !== undefined;
  }

  public get valueLabel(): string {
    return this.labelledValue?.textContent ?? this.value;
  }

  public get valueNode(): Element | string {
    return this.labelledValue ?? this.value;
  }

  public get valueHtmlString(): string {
    if (
      typeof this.labelledValue === 'object' &&
      this.labelledValue instanceof Element
    ) {
      return `html:${htmlEscape(this.labelledValue.outerHTML)}`;
    }

    return this.value;
  }

  public get operatorLabel(): string {
    if (this.operator === TOKEN_OPERATOR_ANY_OF) {
      return '·';
    }

    return this.operator;
  }

  public get htmlValue(): string {
    const trimmed = this.value.trim();

    const quotedValue =
      (trimmed[0] === '"' && trimmed[trimmed.length - 1] === '"') ||
      !trimmed.includes(' ')
        ? trimmed
        : `"${trimmed}"`;

    if (quotedValue === '') {
      return '';
    }

    if (
      (this.type === TOKEN_TYPE_TITLE || this.type === TOKEN_TYPE_ALIAS_NAME) &&
      this.operator === TOKEN_OPERATOR_EQUALS
    ) {
      return quotedValue;
    }

    switch (this.operator) {
      case TOKEN_OPERATOR_EQUALS: {
        return `${this.type}!:${quotedValue}`;
      }

      case TOKEN_OPERATOR_ANY_OF: {
        return `${this.type}:${quotedValue}`;
      }

      case TOKEN_OPERATOR_NEQUALS: {
        return `${this.type}~:${quotedValue}`;
      }

      default: {
        return quotedValue;
      }
    }
  }

  public createElement({
    resolveUrls,
  }: {
    resolveUrls: Partial<Record<'user' | 'label' | 'site', string>>;
  }): HTMLElement {
    return createElement(
      'div',
      {
        'data-controller': 'search-bar--token',
        'data-search-bar--token-data-value': this.toData(),
        'data-search-bar--token-initial-label-value':
          this.valueHtmlString || '',
        'data-token-id': this.id,
        'data-target': 'token',
        'data-computed': this.hasComputedLabel,
        class:
          'border-l border-t border-b border-gray-300 dark:border-gray-700 flex px-1 text-xs whitespace-nowrap button-group rounded-none',
        ...this.resolveUrlAttribute(resolveUrls),
      },
      this.htmlValue,
    );
  }

  private resolveUrlAttribute(
    resolveUrls: Partial<Record<'user' | 'label' | 'site', string>>,
  ): Record<string, string> | null {
    switch (this.type) {
      case TOKEN_TYPE_ASSIGNEE:
      case TOKEN_TYPE_AUTHOR: {
        if (!resolveUrls.user) {
          const meta = document.head.querySelector(
            'meta[name="opus-resolvable-urls"]',
          );
          const content = meta?.getAttribute('content');
          if (!content) {
            return null;
          }

          try {
            const urls = JSON.parse(content) as Record<string, string | null>;
            const url = urls[this.value];
            if (!url) {
              return null;
            }

            return {
              'data-search-bar--token-resolve-url-value': url,
            };
          } catch {
            return null;
          }
        }
        return {
          'data-search-bar--token-resolve-url-value': resolveUrls.user.replace(
            /({id}|%7Bid%7D)/,
            encodeURIComponent(this.value),
          ),
        };
      }

      case TOKEN_TYPE_LABEL: {
        if (!resolveUrls.label) {
          return null;
        }
        return {
          'data-search-bar--token-resolve-url-value': resolveUrls.label.replace(
            /({id}|{identifier}|%7Bid%7D|%7Bidentifier%7D)/,
            encodeURIComponent(toBase64(this.value)),
          ),
        };
      }

      case TOKEN_TYPE_ROLE:
      case TOKEN_TYPE_ALIAS_ASSET_TYPE:
      case TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE: {
        const meta = document.head.querySelector(
          'meta[name="opus-resolvable-urls"]',
        );
        const content = meta?.getAttribute('content');
        if (!content) {
          return null;
        }

        try {
          const urls = JSON.parse(content) as Record<string, string | null>;
          const url = urls[this.value];
          if (!url) {
            return null;
          }

          return {
            'data-search-bar--token-resolve-url-value': url,
          };
        } catch {
          return null;
        }
      }

      case TOKEN_TYPE_SITE:
      case TOKEN_TYPE_ALIAS_LOCATED_AT: {
        if (!resolveUrls.site) {
          return null;
        }
        return {
          'data-search-bar--token-resolve-url-value': resolveUrls.site.replace(
            /({id}|%7Bid%7D)/,
            encodeURIComponent(this.value),
          ),
        };
      }
    }

    return null;
  }

  public toData(): TokenData {
    return {
      id: this.id,
      type: this.type,
      operator: this.operator,
      value: this.value,
    };
  }

  public static fromElement(element: Element) {
    const dataAttributeValue = JSON.parse(
      element.getAttribute('data-search-bar--token-data-value') || '',
    ) as TokenData;
    const initialLabelValue = element.getAttribute(
      'data-search-bar--token-initialLabel-value',
    );

    return this.fromData(dataAttributeValue, initialLabelValue || undefined);
  }

  public static fromData(data: TokenData, initialLabel?: string | Element) {
    return new Token(
      isValidTokenType(data.type) ? data.type : TOKEN_TYPE_INVALID,
      isValidOperator(data.operator) ? data.operator : TOKEN_OPERATOR_INVALID,
      data.value || '',
      initialLabel || undefined,
      data.id || undefined,
    );
  }
}

function toBase64(value: string) {
  if ('function' === typeof btoa) {
    return btoa(value);
  } else if ('Buffer' in globalThis) {
    return Buffer.from(value, 'binary').toString('base64');
  } else {
    throw Error('Cannot generate base64 output');
  }
}
